/**
 * Integrytas Group - Base App (MOBILE)
 * Page: ASF.DIAGNOSTICS — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/AsfDiagnosticsPage.tsx. A DISTINCT
 * presentation (KPI tiles + scannable snapshot cards + bottom-sheet detail) over
 * the SAME read-only data layer — never the desktop grid re-hosted (§16):
 *   - reads via apiDb over asf_snapshot (the captured environment fingerprints),
 *     ordered newest-first and capped, exactly like the desktop query,
 *   - resolves each snapshot's user_id FK to a real display name and its captured
 *     role_ids to translated role names via csm_role_master + _tl against the
 *     ACTIVE locale, so no raw surrogate id / code reaches render (§8, §17.B4).
 * This screen is READ-ONLY: it performs no writes, so it imports neither
 * routed_writes nor any mutation path (§2 — there is nothing to write here).
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, X, Fingerprint, ScanLine, MonitorSmartphone } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { useActiveLocale } from '../../lib/use_active_locale';
import { useFormatDateTime } from '../../lib/format_date';
import {
  MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow,
  MobileDetailRow, MobileFilterChips, MobileStatusPill,
  type MobileLeadTone, type MobilePillTone,
} from '../components';
import type { MobilePageComponentProps } from '../mobile_page_components';

// ---------------------------------------------------------------------------
// Row shape — mirrors the desktop asf_snapshot select exactly (do not invent
// columns). role_ids / role_matrix are captured as JSON, so they arrive typed
// as unknown and are narrowed at use.
// ---------------------------------------------------------------------------

interface SnapRow {
  asf_snapshot_id: number;
  user_id: number | null;
  captured_at: string;
  action: string | null;
  entity_name: string | null;
  record_id: string | null;
  une_workspace_id: number | null;
  screen_code: string | null;
  tab_id: string | null;
  role_ids: unknown;
  fingerprint_hash: string | null;
  ip_address: string | null;
  session_token: string | null;
  session_started_at: string | null;
}

/** A snapshot enriched with resolved display values (never raw ids). */
interface SnapView extends SnapRow {
  user_name: string;
  role_names: string[];
}

interface TlRow { locale_code: string; display_text: string }

const SNAPSHOT_LIMIT = 100;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Narrow an unknown captured JSON value to a numeric id list (role_ids). */
function toNumberIds(value: unknown): number[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
}

/** Short, scannable fingerprint tag — the full hash lives in the detail sheet. */
function shortHash(hash: string | null): string {
  if (!hash) return '—';
  return hash.length > 12 ? `${hash.slice(0, 12)}…` : hash;
}

export function MobileAsfDiagnosticsPage(_props: MobilePageComponentProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<SnapView[]>([]);
  const [filter, setFilter] = useState('');
  const [actionFacet, setActionFacet] = useState<string>('all');
  const [selected, setSelected] = useState<SnapView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('asf_snapshot')
        .select(
          'asf_snapshot_id, user_id, captured_at, action, entity_name, record_id, une_workspace_id, screen_code, tab_id, role_ids, fingerprint_hash, ip_address, session_token, session_started_at',
        )
        .order('captured_at', { ascending: false })
        .limit(SNAPSHOT_LIMIT);
      if (e) throw e;
      const raw = (data ?? []) as SnapRow[];

      // Resolve every user_id FK to a real name so no surrogate id reaches
      // render (§17.B4). One batched lookup keyed by the ids actually present.
      const userIds = Array.from(
        new Set(raw.map((r) => r.user_id).filter((x): x is number => x != null)),
      );
      const userNames: Record<number, string> = {};
      if (userIds.length) {
        const { data: us, error: uErr } = await apiDb
          .from('csm_user_master')
          .select('user_id, display_name, email')
          .in('user_id', userIds);
        if (uErr) throw uErr;
        for (const u of (us ?? []) as { user_id: number; display_name: string | null; email: string }[]) {
          userNames[u.user_id] = u.display_name || u.email || '—';
        }
      }

      // Resolve captured role_ids to translated role names against the active
      // locale (§8, §17.B4) — the fingerprint stores ids, users read names.
      const roleIds = Array.from(new Set(raw.flatMap((r) => toNumberIds(r.role_ids))));
      const roleNames: Record<number, string> = {};
      if (roleIds.length) {
        const { data: rs, error: rErr } = await apiDb
          .from('csm_role_master')
          .select('role_id, internal_code, csm_role_master_tl ( locale_code, display_text )')
          .in('role_id', roleIds);
        if (rErr) throw rErr;
        type RawRole = { role_id: number; internal_code: string; csm_role_master_tl?: TlRow[] };
        for (const r of (rs ?? []) as RawRole[]) {
          const tls = r.csm_role_master_tl ?? [];
          const exact = tls.find((tl) => tl.locale_code === activeLocale);
          const fb = tls.find((tl) => tl.locale_code === 'EN_US') ?? tls[0];
          roleNames[r.role_id] = exact?.display_text ?? fb?.display_text ?? r.internal_code;
        }
      }

      const views: SnapView[] = raw.map((r) => ({
        ...r,
        user_name: r.user_id != null ? (userNames[r.user_id] ?? t('mobile.asf.unknown_user', 'Unknown user')) : t('mobile.asf.system', 'System'),
        role_names: toNumberIds(r.role_ids).map((id) => roleNames[id] ?? String(id)),
      }));
      setRows(views);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Re-run on mount and whenever the active locale changes (role labels follow it).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload(); }, [activeLocale]);

  // Distinct action codes present, for the facet chips (derived, never hardcoded).
  const actions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) if (r.action) set.add(r.action);
    return Array.from(set).sort();
  }, [rows]);

  // Aggregate KPI totals across the loaded window (§ HomeScreen KPI pattern).
  const kpis = useMemo(() => {
    const users = new Set<number>();
    const entities = new Set<string>();
    for (const r of rows) {
      if (r.user_id != null) users.add(r.user_id);
      if (r.entity_name) entities.add(r.entity_name);
    }
    return { snapshots: rows.length, users: users.size, entities: entities.size, actions: actions.length };
  }, [rows, actions]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (actionFacet !== 'all' && r.action !== actionFacet) return false;
      if (!q) return true;
      return (
        (r.entity_name ?? '').toLowerCase().includes(q) ||
        (r.record_id ?? '').toLowerCase().includes(q) ||
        (r.screen_code ?? '').toLowerCase().includes(q) ||
        (r.action ?? '').toLowerCase().includes(q) ||
        (r.ip_address ?? '').toLowerCase().includes(q) ||
        (r.fingerprint_hash ?? '').toLowerCase().includes(q) ||
        r.user_name.toLowerCase().includes(q)
      );
    });
  }, [rows, filter, actionFacet]);

  // Localise the captured action code (§17.B11 — label via i18n, never the bare
  // literal), falling back to the raw code for values without a bundle key.
  const actionLabel = (action: string | null): string =>
    action ? t(`mobile.asf.action.${action}`, action) : t('mobile.asf.action.none', 'Event');

  // Colour the lead + pill by action family. The stored value stays the code;
  // only the tone is derived.
  const actionTone = (action: string | null): { lead: MobileLeadTone; pill: MobilePillTone } => {
    const a = (action ?? '').toUpperCase();
    if (a.includes('INSERT') || a.includes('CREATE')) return { lead: 'green', pill: 'ok' };
    if (a.includes('DELETE') || a.includes('REMOVE')) return { lead: 'red', pill: 'no' };
    if (a.includes('UPDATE') || a.includes('MODIFY') || a.includes('CHANGE')) return { lead: 'blue', pill: 'info' };
    return { lead: 'slate', pill: 'info' };
  };

  const targetText = (r: SnapView): string => {
    if (!r.entity_name) return t('mobile.asf.no_target', 'No target');
    return r.record_id ? `${r.entity_name} #${r.record_id}` : r.entity_name;
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.asf.title', 'App-State Fingerprints')}
        description={t('mobile.asf.description', 'Captured environment snapshots recorded at each audited event.')}
      />

      <MobileStatStrip
        stats={[
          { value: kpis.snapshots.toLocaleString(), label: t('mobile.asf.kpi.snapshots', 'Snapshots') },
          { value: kpis.users.toLocaleString(), label: t('mobile.asf.kpi.users', 'Users') },
          { value: kpis.entities.toLocaleString(), label: t('mobile.asf.kpi.entities', 'Entities') },
          { value: kpis.actions.toLocaleString(), label: t('mobile.asf.kpi.actions', 'Actions') },
        ]}
      />

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.asf.search', 'Search snapshots')}
          aria-label={t('mobile.asf.search', 'Search snapshots')}
        />
      </div>

      <MobileFilterChips
        chips={[
          { value: 'all', label: t('mobile.asf.all', 'All') },
          ...actions.map((a) => ({ value: a, label: actionLabel(a) })),
        ]}
        isActive={(v) => actionFacet === v}
        onSelect={(v) => setActionFacet(v)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.asf.none', 'No snapshots match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => {
            const tone = actionTone(r.action);
            return (
              <MobileCardRow
                key={r.asf_snapshot_id}
                lead={<Fingerprint />}
                leadTone={tone.lead}
                title={targetText(r)}
                subtitle={`${r.user_name} · ${formatDateTime(r.captured_at)}`}
                trailing={<MobileStatusPill tone={tone.pill}>{actionLabel(r.action)}</MobileStatusPill>}
                chevron
                onClick={() => setSelected(r)}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet (read-only reconstruction of the captured environment) */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => setSelected(null)} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.asf.detail', 'Snapshot detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><Fingerprint size={20} /></span>
              <span>
                <div className="m-nm">{targetText(selected)}</div>
                <div className="m-em">{formatDateTime(selected.captured_at)}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.asf.field.action', 'Action')}
                value={<MobileStatusPill tone={actionTone(selected.action).pill}>{actionLabel(selected.action)}</MobileStatusPill>}
              />
              <MobileDetailRow label={t('mobile.asf.field.user', 'User')} value={selected.user_name} />
              {/* fk-display-ok: screen_code / tab_id are the raw app-state fingerprint tags captured at the event, not FKs */}
              <MobileDetailRow
                label={t('mobile.asf.field.screen', 'Screen')}
                value={`${selected.screen_code ?? '—'}${selected.tab_id ? ` / ${selected.tab_id}` : ''}`}
              />
              <MobileDetailRow
                label={t('mobile.asf.field.roles', 'Roles at capture')}
                value={selected.role_names.length ? selected.role_names.join(', ') : t('common.none', 'None')}
              />
              {/* fk-display-ok: fingerprint_hash is the diagnostic identity this screen exists to surface */}
              <MobileDetailRow
                label={t('mobile.asf.field.fingerprint', 'Fingerprint')}
                value={<span style={{ wordBreak: 'break-all' }}><ScanLine size={14} /> {selected.fingerprint_hash ?? '—'}</span>}
              />
              {/* fk-display-ok: ip_address is a raw captured network identifier, not a lookup code */}
              <MobileDetailRow label={t('mobile.asf.field.ip', 'IP address')} value={selected.ip_address ?? '—'} />
              {/* fk-display-ok: session_token is the raw captured session identifier */}
              <MobileDetailRow
                label={t('mobile.asf.field.device', 'Session')}
                value={<span style={{ wordBreak: 'break-all' }}><MonitorSmartphone size={14} /> {shortHash(selected.session_token)}</span>}
              />
              <MobileDetailRow
                label={t('mobile.asf.field.session_started', 'Session started')}
                value={selected.session_started_at ? formatDateTime(selected.session_started_at) : '—'}
              />
            </MobileContentCard>
            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
